import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { importanceMeta, STATUS_LABEL, IMPORTANCE_KEYS, fmtDateTime } from "@/lib/taskUi";
import { DataTable } from "@/components/common/DataTable";
import { Star, CheckCircle2, Circle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const isDone = (t) => t.status === "completed" || t.status === "cancelled";

function call(fn, onChanged) {
  return async (e) => {
    e.stopPropagation();
    try { await fn(); onChanged?.(); } catch (err) { toast.error(apiError(err)); }
  };
}

const STATUS_ORDER = ["not_started", "in_progress", "waiting", "completed", "cancelled"];
const SORT_VALUE = {
  title: (t) => (t.title || "").toLowerCase(),
  importance: (t) => IMPORTANCE_KEYS.indexOf(t.importance),
  due: (t) => (t.due_date ? new Date(t.due_date).getTime() : null),
  status: (t) => STATUS_ORDER.indexOf(t.status),
  category: (t) => (t.category_label || "").toLowerCase() || null,
  person: (t) => (t.person_name || "").toLowerCase() || null,
  project: (t) => (t.project_name || "").toLowerCase() || null,
  assignee: (t) => (t.assignee_name || "").toLowerCase() || null,
};

function sortRows(rows, field, order) {
  const get = SORT_VALUE[field];
  if (!get) return rows;
  const dir = order === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = get(a), y = get(b);
    const xe = x == null || x === -1, ye = y == null || y === -1;
    if (xe || ye) return xe === ye ? 0 : xe ? 1 : -1;
    return x < y ? -dir : x > y ? dir : 0;
  });
}

function buildColumns({ onChanged, showTop10, showAssignee }) {
  const cols = [
    { key: "__done", header: "", render: (t) => (
      <button onClick={call(() => api.patch(`/tasks/${t.id}`, { status: isDone(t) ? "in_progress" : "completed" }), onChanged)}
        data-testid={`task-complete-${t.id}`} aria-label={isDone(t) ? "Reopen task" : "Complete task"}>
        {isDone(t) ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-300 hover:text-brand" />}
      </button>) },
    { key: "title", header: "Task", width: 300, render: (t) => (
      <span data-testid={`task-row-${t.id}`} title={t.title}
        className={`block truncate ${isDone(t) ? "line-through text-slate-400" : "text-slate-800 font-medium"}`}>{t.title}</span>) },
    { key: "importance", header: "Importance", width: 130, render: (t) => {
      const im = importanceMeta(t.importance);
      return <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${im.badge}`}><im.icon className="w-3 h-3 mr-0.5" />{im.label}</span>;
    } },
    { key: "due", header: "Due", width: 190, render: (t) => (
      <span className="text-xs whitespace-nowrap">
        {t.is_overdue && <span data-testid={`task-overdue-${t.id}`} className="font-semibold text-rose-600 inline-flex items-center gap-1 mr-1.5"><AlertTriangle className="w-3.5 h-3.5" />{t.days_overdue}d</span>}
        <span className="text-slate-500">{t.due_date ? fmtDateTime(t.due_date) : "No due date"}</span>
      </span>) },
    { key: "status", header: "Status", width: 130, render: (t) => <span className="text-[11px] rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 whitespace-nowrap">{STATUS_LABEL[t.status] || t.status}</span> },
    { key: "category", header: "Category", render: (t) => <span className="text-slate-600">{t.category_label || "—"}</span> },
    { key: "person", header: "Person", render: (t) => <span className="text-slate-600">{t.person_name || "—"}</span> },
    { key: "project", header: "Project", render: (t) => <span className="text-slate-600">{t.project_name || "—"}</span> },
  ];
  if (showAssignee) cols.push({ key: "assignee", header: "Assignee", render: (t) => <span className="text-slate-600">{t.assignee_name || "Not assigned"}</span> });
  if (showTop10) cols.push({ key: "__top10", header: "", render: (t) => !isDone(t) && (
    <button onClick={call(() => api.post(`/tasks/${t.id}/top10`, { on: !t.in_top10 }), onChanged)}
      data-testid={`task-top10-${t.id}`} title="Add to Daily Top 10" aria-label="Toggle Daily Top 10">
      <Star className={`w-4 h-4 ${t.in_top10 ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-400"}`} />
    </button>) });
  return cols.map((c) => (SORT_VALUE[c.key] ? { ...c, sortKey: c.key } : c));
}

export function TaskTable({ rows, onChanged, showTop10 = true, showAssignee = false, resizeKey, testId, empty }) {
  const navigate = useNavigate();
  const store = `tr-task-sort-${testId || resizeKey || "tasks"}`;
  const [sort, setSort] = useState(() => { try { return JSON.parse(sessionStorage.getItem(store)) || {}; } catch { return {}; } });
  const onSort = (f) => {
    const next = sort.field !== f ? { field: f, order: "asc" } : sort.order === "asc" ? { field: f, order: "desc" } : {};
    setSort(next);
    sessionStorage.setItem(store, JSON.stringify(next));
  };
  const sorted = useMemo(() => (sort.field ? sortRows(rows || [], sort.field, sort.order) : rows), [rows, sort]);
  const viewExtra = { get: () => ({ sort }), apply: (x) => { const s = x?.sort || {}; setSort(s); sessionStorage.setItem(store, JSON.stringify(s)); } };
  return (
    <DataTable columns={buildColumns({ onChanged, showTop10, showAssignee })} rows={sorted} resizeKey={resizeKey}
      sortField={sort.field} sortOrder={sort.order} onSort={onSort} viewExtra={viewExtra}
      onRowClick={(t) => navigate(`/tasks/detail/${t.id}`, { state: { from: window.location.pathname + window.location.search, label: window.location.pathname.startsWith("/tasks") ? "Tasks" : "Back" } })} testId={testId} empty={empty} />
  );
}
